import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { PriceBoardRow } from 'api/types';
import {
  MENU, money, planBulk, changeTable, type BoardColumn, type BulkOperation, type BulkPreviewLine, type BulkSkip, type PendingMap,
} from 'components/modules/dineops-module/masters/priceBoardMath';

/**
 * Price Board — Change prices (DineOps-Item-Pricing.md D2).
 *
 * Pick the dishes (the rows ticked on the board, or every dish matching the
 * board's filters), the columns (Menu and/or outlets), the operation and the
 * rounding. A Before → After preview comes first. Applying puts the changes on
 * the board as unsaved; nothing is saved until Save on the board.
 */

interface Props {
  isOpen: boolean;
  /** The rows ticked on the board, sizes included. */
  selectedRows: PriceBoardRow[];
  /** How many dishes match the board's filters. */
  matchingCount: number;
  /** Loads every dish matching the board's filters (all pages). */
  loadMatching: () => Promise<PriceBoardRow[]>;
  /** The board's columns: Menu, then the shown outlets. */
  columns: { id: BoardColumn; name: string }[];
  pending: PendingMap;
  onClose: () => void;
  onApply: (lines: BulkPreviewLine[]) => void;
}

const OPERATIONS: { value: BulkOperation; label: string; hint: string }[] = [
  { value: 'percent', label: 'Change by %', hint: 'e.g. 10 raises by 10%, -5 lowers by 5%' },
  { value: 'amount', label: 'Change by an amount', hint: 'e.g. 20 adds 20, -10 takes off 10' },
  { value: 'set', label: 'Set to a price', hint: 'every chosen cell gets this price' },
  { value: 'round', label: 'Round only', hint: 'round the current prices' },
];

const ROUNDING = [
  { value: '0', label: 'No rounding' },
  { value: '1', label: 'Nearest 1' },
  { value: '5', label: 'Nearest 5' },
  { value: '10', label: 'Nearest 10' },
];

/** The preview shows this many lines; the rest are applied all the same. */
const PREVIEW_LINES = 300;

const DineOpsPriceBoardBulkModal: React.FC<Props> = ({
  isOpen, selectedRows, matchingCount, loadMatching, columns, pending, onClose, onApply,
}) => {
  const selectedDishes = useMemo(() => selectedRows.filter((r) => !r.parentItemId).length
    + new Set(selectedRows.filter((r) => r.parentItemId && !selectedRows.some((p) => p.itemId === r.parentItemId)).map((r) => r.parentItemId)).size,
  [selectedRows]);

  const [scope, setScope] = useState<'selected' | 'matching'>('selected');
  const [chosen, setChosen] = useState<string[]>([]);
  const [operation, setOperation] = useState<BulkOperation>('percent');
  const [valueText, setValueText] = useState('');
  const [rounding, setRounding] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ lines: BulkPreviewLine[]; skipped: BulkSkip[] } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setScope(selectedRows.length > 0 ? 'selected' : 'matching');
    setChosen(columns.map((c) => c.id));
    setOperation('percent');
    setValueText('');
    setRounding('0');
    setError(null);
    setPreview(null);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const handlePreview = async () => {
    setError(null);
    const cols = columns.filter((c) => chosen.includes(c.id));
    if (cols.length === 0) { setError('Choose at least one column.'); return; }
    const step = Number(rounding);
    let value = 0;
    if (operation === 'round') {
      if (step === 0) { setError('Choose the rounding: nearest 1, 5 or 10.'); return; }
    } else {
      const t = valueText.replace(/,/g, '').trim();
      if (!/^-?\d+(\.\d{1,2})?$/.test(t)) { setError('Enter a number, with at most 2 decimals.'); return; }
      value = Number(t);
      if (operation === 'set' && value < 0) { setError('A price cannot be negative.'); return; }
      if (operation === 'percent' && value <= -100) { setError('A cut of 100% or more would make prices zero or less.'); return; }
      if (value === 0 && operation !== 'set') { setError('Enter a change other than zero.'); return; }
    }

    setBusy(true);
    try {
      const rows = scope === 'selected' ? selectedRows : await loadMatching();
      const plan = planBulk(rows, cols, { operation, value, rounding: step }, pending);
      if (plan.lines.length === 0 && plan.skipped.length === 0) { setError('No price would change. Check the dishes and columns chosen.'); return; }
      setPreview(plan);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'The dishes could not be loaded.');
    } finally {
      setBusy(false);
    }
  };

  const shown = (n: number | null, charged: number) => (n === null ? `${money(charged)} ·` : money(n));

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto', maxWidth: 820 }}>
        {busy && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Working out the new prices...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{preview ? 'Change prices — preview' : 'Change prices'}</h2>
          <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}

          {!preview && (
            <>
              <div className="form-group">
                <label className="form-label">Dishes</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', opacity: selectedRows.length ? 1 : 0.5 }}>
                    <input type="radio" name="pb-scope" checked={scope === 'selected'} disabled={selectedRows.length === 0}
                      onChange={() => setScope('selected')} />
                    The dishes ticked on the board ({selectedDishes})
                  </label>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="radio" name="pb-scope" checked={scope === 'matching'} onChange={() => setScope('matching')} />
                    Every dish matching the board's filters ({matchingCount})
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Columns</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 14 }}>
                  {columns.map((c) => (
                    <label key={c.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="checkbox" checked={chosen.includes(c.id)}
                        onChange={(e) => setChosen((p) => (e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id)))} />
                      {c.name}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  {chosen.includes(MENU)
                    ? 'Outlets that follow the menu price keep following it, at the new menu price.'
                    : 'Outlets that follow the menu price get a price of their own.'}
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <SyutSelect label="Change" options={OPERATIONS.map((o) => ({ value: o.value, label: o.label }))} value={operation}
                    onChange={(v) => setOperation(((v as string) || 'percent') as BulkOperation)} isMulti={false} size="sm"
                    menuPortalTarget={document.body} />
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{OPERATIONS.find((o) => o.value === operation)?.hint}</div>
                </div>
                {operation !== 'round' && (
                  <div className="form-group">
                    <label className="form-label" htmlFor="pb-value">{operation === 'percent' ? 'Percent' : operation === 'amount' ? 'Amount' : 'New price'}</label>
                    <input id="pb-value" className="form-input" inputMode="decimal" value={valueText} autoFocus
                      onChange={(e) => setValueText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void handlePreview(); }} />
                  </div>
                )}
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <SyutSelect label="Rounding" options={ROUNDING} value={rounding} onChange={(v) => setRounding((v as string) || '0')}
                    isMulti={false} size="sm" menuPortalTarget={document.body} />
                </div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 0 }}>
                Dishes with sizes are changed on their size rows. Open-priced dishes and outlets that do not sell a dish are left out.
              </p>
            </>
          )}

          {preview && (
            <>
              <p style={{ marginTop: 0, fontSize: 14 }}>
                <strong>{preview.lines.length}</strong> price{preview.lines.length === 1 ? '' : 's'} will change on the board.
                Nothing is saved until you click <strong>Save</strong> on the board.
              </p>
              {preview.lines.length > 0 && (
                <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                  <table style={changeTable.table}>
                    <thead style={changeTable.head}>
                      <tr><th style={changeTable.th}>Dish</th><th style={changeTable.th}>Column</th><th style={{ ...changeTable.th, textAlign: 'right' }}>Before</th><th style={{ ...changeTable.th, textAlign: 'right' }}>After</th></tr>
                    </thead>
                    <tbody>
                      {preview.lines.slice(0, PREVIEW_LINES).map((l) => (
                        <tr key={l.key}>
                          <td style={changeTable.td}>{l.row.itemName}</td>
                          <td style={changeTable.td}>{l.columnName}</td>
                          <td style={{ ...changeTable.td, textAlign: 'right', color: 'var(--text-secondary)' }}>{shown(l.current, l.currentCharged)}</td>
                          <td style={{ ...changeTable.td, textAlign: 'right', fontWeight: 600 }}>{money(l.after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {preview.lines.length > PREVIEW_LINES && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Showing the first {PREVIEW_LINES}. All {preview.lines.length} are applied.
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>A price with · follows the menu price.</div>
              {preview.skipped.length > 0 && (
                <div className="form-error-message" style={{ marginTop: 10 }}>
                  Left out: {preview.skipped.slice(0, 10).map((s) => `${s.itemName} (${s.columnName}) ${s.reason}`).join('; ')}
                  {preview.skipped.length > 10 ? ` and ${preview.skipped.length - 10} more.` : '.'}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {!preview ? (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void handlePreview()} disabled={busy}>Preview</button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setPreview(null)}>Back</button>
              <button className="btn btn-primary" disabled={preview.lines.length === 0}
                onClick={() => { onApply(preview.lines); onClose(); }}>
                Put on the board
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DineOpsPriceBoardBulkModal;
